import { Button, TextField } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { merge } from 'lodash';
import {
  BarChartSpec,
  DEFAULT_TOPLIST_BARS_LIMIT,
  isPercentUnit,
  MAX_BARS_LIMIT_DESCRIPTION,
  ModeOption,
  SortOption,
  UNIT_SETTINGS_NAME,
  UnitOptions,
} from '@/core';
import {
  ModeSelector,
  ModeSelectorProps,
  OptionsEditorColumn,
  OptionsEditorControl,
  OptionsEditorGrid,
  OptionsEditorGroup,
  SORT_OPTIONS,
  SortSelector,
  SortSelectorProps,
  ThresholdsEditorProps,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import { BarChartThresholdsEditor } from './BarChartThresholdsEditor';
import {
  BarChartOptionsEditorProps,
  DEFAULT_CALCULATION,
  DEFAULT_MODE,
  DEFAULT_SORT,
  DEFAULT_UNIT,
} from './bar-chart-model';

export function BarChartOptionsEditorSettings(props: BarChartOptionsEditorProps) {
  const { onChange, value } = props;

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit: UnitOptions) => {
    onChange(
      produce(value, (draft: BarChartSpec) => {
        draft.unit = newUnit;
      })
    );
  };

  const handleSortChange: SortSelectorProps<SortOption>['onChange'] = (newSort?: SortOption) => {
    onChange(
      produce(value, (draft: BarChartSpec) => {
        draft.sort = newSort;
      })
    );
  };

  const handleModeChange: ModeSelectorProps['onChange'] = (newMode: ModeOption) => {
    onChange(
      produce(value, (draft: BarChartSpec) => {
        draft.mode = newMode;
        // Set threshold mode to Percent when newMode is 'percentage'
        if (newMode === 'percentage') {
          if (!draft.thresholds) {
            draft.thresholds = {
              mode: 'Percent',
              steps: [],
            };
          } else {
            draft.thresholds.mode = 'Percent';
          }
        }
      })
    );
  };

  const handleThresholdsChange: ThresholdsEditorProps['onChange'] = (thresholds) => {
    onChange(
      produce(value, (draft: BarChartSpec) => {
        draft.thresholds = thresholds;
      })
    );
  };

  const handleResetSettings: React.MouseEventHandler<HTMLButtonElement> = () => {
    onChange(
      produce(value, (draft: BarChartSpec) => {
        draft.calculation = DEFAULT_CALCULATION;
        draft.unit = DEFAULT_UNIT;
        draft.sort = DEFAULT_SORT;
        draft.mode = DEFAULT_MODE;
        draft.limit = undefined;
        draft.thresholds = undefined;
      })
    );
  };

  // ensures decimal_places defaults to correct value
  const unit = merge({}, DEFAULT_UNIT, value.unit);

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Misc">
          <ModeSelector value={value.mode} onChange={handleModeChange} disablePercentageMode={isPercentUnit(unit)} />
          <SortSelector value={value.sort} onChange={handleSortChange} options={SORT_OPTIONS} />
          <OptionsEditorControl
            label="Limit"
            description={MAX_BARS_LIMIT_DESCRIPTION}
            control={
              <TextField
                fullWidth
                type="number"
                value={value.limit}
                onChange={(e) => {
                  const newValue = e.target.value ? Number(e.target.value) : undefined;
                  onChange({
                    ...value,
                    limit: newValue,
                  });
                }}
                placeholder={String(DEFAULT_TOPLIST_BARS_LIMIT)}
              />
            }
          />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector value={unit} onChange={handleUnitChange} disabled={value.mode === 'percentage'} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <BarChartThresholdsEditor thresholds={value.thresholds} onChange={handleThresholdsChange} />
        <OptionsEditorGroup title="Reset Settings">
          <Button variant="secondary" onClick={handleResetSettings}>
            Reset to defaults
          </Button>
        </OptionsEditorGroup>
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
